import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';

const ASSET_TYPES = [
  { key: 'solar', label: 'Solar PV' },
  { key: 'wind', label: 'Wind' },
  { key: 'bess', label: 'BESS' },
  { key: 'hybrid', label: 'Hybrid' },
  { key: 'consumer_site', label: 'Consumer Site' },
];

const OPERATIONAL_STATUSES = [
  { key: 'planned', label: 'Planned' },
  { key: 'construction', label: 'Under Construction' },
  { key: 'commissioning', label: 'Commissioning' },
  { key: 'active', label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'decommissioned', label: 'Decommissioned' },
];

interface FormData {
  assetName: string;
  assetType: string;
  eicCode: string;
  eicWCode: string;
  installedCapacityMw: string;
  region: string;
  locationAddress: string;
  latitude: string;
  longitude: string;
  commissioningDate: string;
  operationalStatus: string;
}

const initialForm: FormData = {
  assetName: '',
  assetType: 'solar',
  eicCode: '',
  eicWCode: '',
  installedCapacityMw: '',
  region: '',
  locationAddress: '',
  latitude: '',
  longitude: '',
  commissioningDate: '',
  operationalStatus: 'planned',
};

const WIZARD_STEPS = [
  { num: 1, label: 'Basic Info', icon: 'ri-information-line' },
  { num: 2, label: 'Technical Spec', icon: 'ri-settings-3-line' },
  { num: 3, label: 'Market Config', icon: 'ri-line-chart-line' },
  { num: 4, label: 'Metering / SCADA', icon: 'ri-dashboard-line' },
  { num: 5, label: 'Forecasting', icon: 'ri-cloud-line' },
  { num: 6, label: 'Financial Terms', icon: 'ri-money-dollar-circle-line' },
];

export default function AssetCreatePage() {
  const { t } = useTranslation();
  const { counterpartyId } = useParams<{ counterpartyId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.assetName.trim()) e.assetName = t('ac_err_name');
    if (!form.eicCode.trim()) e.eicCode = t('ac_err_eic');
    if (!form.installedCapacityMw.trim()) {
      e.installedCapacityMw = t('ac_err_capacity');
    } else if (isNaN(Number(form.installedCapacityMw)) || Number(form.installedCapacityMw) <= 0) {
      e.installedCapacityMw = 'Must be a positive number';
    }
    if (!form.region.trim()) e.region = 'Region is required';
    if (!form.locationAddress.trim()) e.locationAddress = t('ac_err_address');
    if (form.latitude && (isNaN(Number(form.latitude)) || Number(form.latitude) < -90 || Number(form.latitude) > 90)) {
      e.latitude = 'Invalid latitude (-90 to 90)';
    }
    if (form.longitude && (isNaN(Number(form.longitude)) || Number(form.longitude) < -180 || Number(form.longitude) > 180)) {
      e.longitude = 'Invalid longitude (-180 to 180)';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      addToast('Please fix the errors in the form', 'warning');
      return;
    }
    if (!counterpartyId) {
      addToast(t('ac_err_cp_id'), 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        assetName: form.assetName.trim(),
        assetType: form.assetType,
        eicCode: form.eicCode.trim(),
        eicWCode: form.eicWCode.trim() || undefined,
        installedCapacityMw: Number(form.installedCapacityMw),
        region: form.region.trim(),
        locationAddress: form.locationAddress.trim(),
        operationalStatus: form.operationalStatus,
      };
      if (form.latitude) payload.latitude = Number(form.latitude);
      if (form.longitude) payload.longitude = Number(form.longitude);
      if (form.commissioningDate) {
        payload.commissioningDate = `${form.commissioningDate}T00:00:00.000Z`;
      }

      const result = await api.post<{ id: string }>(`/counterparties/${counterpartyId}/assets`, payload);
      addToast('Asset created! Now fill in the technical specification.', 'success');
      navigate(`/assets/${result.id}?tab=technical&wizard=true`);
    } catch (err) {
      const message = getApiErrorMessage(err, t('ac_err_create'));
      addToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (key: keyof FormData) =>
    `w-full px-3 py-2.5 rounded-md text-sm border transition-colors outline-none ${
      errors[key] ? 'border-[var(--err)]' : 'border-[var(--line)] focus:border-[var(--line-strong)]'
    }`;

  return (
    <DashboardLayout breadcrumb={[t('cp_section'), 'New Asset']}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm mb-3 cursor-pointer hover:underline"
            style={{ color: '#6B6B70' }}
          >
            <i className="ri-arrow-left-line"></i> Back
          </button>
          <h1 className="text-2xl font-bold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
            Add New Asset
          </h1>
          <p className="text-sm mt-1" style={{ color: '#6B6B70' }}>
            Step 1 of 6 — Basic asset information
          </p>
        </div>

        {/* Wizard progress */}
        <div className="bg-white rounded-xl p-4 mb-5" style={{ border: '1px solid #E5E5E8' }}>
          <div className="flex items-center gap-0 overflow-x-auto">
            {WIZARD_STEPS.map((step, idx) => (
              <div key={step.num} className="flex items-center flex-shrink-0">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{
                    background: step.num === 1 ? 'var(--surface-2)' : 'transparent',
                  }}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{
                      background: step.num === 1 ? 'var(--accent)' : '#F5F5F7',
                      color: step.num === 1 ? '#fff' : '#6B6B70',
                    }}
                  >
                    {step.num}
                  </div>
                  <span
                    className="text-xs font-medium whitespace-nowrap"
                    style={{ color: step.num === 1 ? 'var(--accent)' : '#6B6B70' }}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < WIZARD_STEPS.length - 1 && (
                  <div className="w-6 h-px mx-1 flex-shrink-0" style={{ background: '#E5E5E8' }}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl p-6 md:p-8" style={{ border: '1px solid #E5E5E8' }}>
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
              <i className="ri-information-line text-sm" style={{ color: 'var(--accent)' }}></i>
            </div>
            <div>
              <h2 className="text-base font-semibold" style={{ color: '#1A1A1A' }}>Basic Asset Information</h2>
              <p className="text-xs" style={{ color: '#6B6B70' }}>General identification and location data</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Asset Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                Asset Name <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.assetName}
                onChange={(e) => handleChange('assetName', e.target.value)}
                placeholder="e.g. SES Tabaki 5MW"
                className={inputClass('assetName')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              {errors.assetName && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.assetName}</p>}
            </div>

            {/* Asset Type */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                Asset Type <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <select
                value={form.assetType}
                onChange={(e) => handleChange('assetType', e.target.value)}
                className={inputClass('assetType')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              >
                {ASSET_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* EIC Type X — Party code (may repeat across multiple installations of the same counterparty) */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                EIC Code, Type X (Party) <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.eicCode}
                onChange={(e) => handleChange('eicCode', e.target.value)}
                placeholder="16-character party EIC, e.g. 62X..."
                className={inputClass('eicCode')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              <p className="text-[11px] mt-1" style={{ color: '#9B9BA4' }}>
                Identifies the counterparty / market participant. May repeat across multiple SES of the same owner.
              </p>
              {errors.eicCode && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.eicCode}</p>}
            </div>

            {/* EIC Type W — Resource Object (unique per installation) */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                EIC Code, Type W (Resource)
              </label>
              <input
                type="text"
                value={form.eicWCode}
                onChange={(e) => handleChange('eicWCode', e.target.value)}
                placeholder="16-character resource EIC, e.g. 62W..."
                className={inputClass('eicWCode')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              <p className="text-[11px] mt-1" style={{ color: '#9B9BA4' }}>
                Identifies this specific installation. Must be unique across all assets.
              </p>
              {errors.eicWCode && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.eicWCode}</p>}
            </div>

            {/* Capacity */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                Installed Capacity (MW) <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={form.installedCapacityMw}
                onChange={(e) => handleChange('installedCapacityMw', e.target.value)}
                placeholder="e.g. 5.0"
                className={inputClass('installedCapacityMw')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              {errors.installedCapacityMw && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.installedCapacityMw}</p>}
            </div>

            {/* Region */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                Region <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.region}
                onChange={(e) => handleChange('region', e.target.value)}
                placeholder="e.g. Odesa Oblast"
                className={inputClass('region')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              {errors.region && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.region}</p>}
            </div>

            {/* Location Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                Location Address <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.locationAddress}
                onChange={(e) => handleChange('locationAddress', e.target.value)}
                placeholder="Full physical address of the asset"
                className={inputClass('locationAddress')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              {errors.locationAddress && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.locationAddress}</p>}
            </div>

            {/* Latitude */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>Latitude</label>
              <input
                type="number"
                step="0.000001"
                value={form.latitude}
                onChange={(e) => handleChange('latitude', e.target.value)}
                placeholder="e.g. 46.4825"
                className={inputClass('latitude')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              {errors.latitude && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.latitude}</p>}
            </div>

            {/* Longitude */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>Longitude</label>
              <input
                type="number"
                step="0.000001"
                value={form.longitude}
                onChange={(e) => handleChange('longitude', e.target.value)}
                placeholder="e.g. 30.7233"
                className={inputClass('longitude')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
              {errors.longitude && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.longitude}</p>}
            </div>

            {/* Commissioning Date */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>Commissioning Date</label>
              <input
                type="date"
                value={form.commissioningDate}
                onChange={(e) => handleChange('commissioningDate', e.target.value)}
                className={inputClass('commissioningDate')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              />
            </div>

            {/* Operational Status */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1A1A1A' }}>
                Operational Status <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <select
                value={form.operationalStatus}
                onChange={(e) => handleChange('operationalStatus', e.target.value)}
                className={inputClass('operationalStatus')}
                style={{ background: '#fff', color: '#1A1A1A' }}
              >
                {OPERATIONAL_STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-8 pt-6" style={{ borderTop: '1px solid #E5E5E8' }}>
            <button
              onClick={() => navigate(-1)}
              className="px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
              style={{ border: '1px solid #E5E5E8', color: '#1A1A1A' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Creating...
                </>
              ) : (
                <>
                  Create Asset &amp; Continue
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
